import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Phone, MessageCircle, MapPin, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Footer() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginTab, setLoginTab] = useState("student");
  const [loginForm, setLoginForm] = useState({ username: "", password: "", phone: "" });
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async () => {
    setIsLoggingIn(true);
    try {
      if (loginTab === "student") {
        const response = await axios.post(`${API}/student/login`, {
          phone: loginForm.phone,
          password: loginForm.password
        });
        if (response.data.success) {
          toast.success("Student login successful!");
          localStorage.setItem("studentId", response.data.student.id);
          localStorage.setItem("studentName", response.data.student.name);
          window.location.href = "/student";
        }
      } else if (loginTab === "teacher") {
        const response = await axios.post(`${API}/teacher/login`, {
          phone: loginForm.phone,
          password: loginForm.password
        });
        if (response.data.success) {
          toast.success("Teacher login successful!");
          localStorage.setItem("teacherId", response.data.teacher.id);
          localStorage.setItem("teacherName", response.data.teacher.name);
          window.location.href = "/teacher";
        }
      } else if (loginTab === "admin") {
        const response = await axios.post(`${API}/admin/login`, {
          username: loginForm.username,
          password: loginForm.password
        });
        if (response.data.success) {
          toast.success("Admin login successful!");
          localStorage.setItem("admin", "true");
          window.location.href = "/admin";
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <>
      {/* Footer - Professional 4-Column Layout */}
      <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            
            {/* Column 1 - Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="MUAS Academy Logo" className="w-14 h-14 rounded-full object-cover" />
                <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent" style={{ fontFamily: "'Playfair Display', serif" }}>
                  MUAS Academy
                </h3>
              </div>
              <p className="text-pink-300 text-sm font-medium">Ranchi's Premier Makeup & Beauty Training Institute</p>
              <p className="text-gray-400 text-sm leading-relaxed">
                Transform your passion into a profession. We offer comprehensive makeup courses, bridal training, and beauty education with industry-recognized certification.
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 p-2"
                  onClick={() => window.open("https://www.instagram.com/aradhanashreyamakeup/?hl=en", "_blank")}
                >
                  <Instagram className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 p-2"
                  onClick={() => window.open("https://wa.me/916201542274", "_blank")}
                >
                  <MessageCircle className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 p-2"
                  onClick={() => window.open("tel:+916201542274")}
                >
                  <Phone className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Column 2 - Quick Links */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-pink-400 transition-colors">Home</Link></li>
                <li><Link to="/about-us" className="text-gray-400 hover:text-pink-400 transition-colors">About Us</Link></li>
                <li><Link to="/courses" className="text-gray-400 hover:text-pink-400 transition-colors">Courses</Link></li>
                <li><Link to="/bridal-services" className="text-gray-400 hover:text-pink-400 transition-colors">Bridal Services</Link></li>
                <li><Link to="/bridal-portfolio" className="text-gray-400 hover:text-pink-400 transition-colors">Gallery</Link></li>
                <li>
                  <button 
                    onClick={() => setShowLoginModal(true)}
                    className="text-gray-400 hover:text-pink-400 transition-colors"
                  >
                    Student/Teacher/Admin Login
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 3 - Contact Info */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Contact Info</h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-400">
                    C-127, Harmu Housing Colony,<br />
                    Harmu Ranchi 834002,<br />
                    Jharkhand, India
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <div className="text-gray-400">
                    <a href="tel:+916201542274" className="hover:text-pink-400 transition-colors">+91 6201542274</a><br />
                    <a href="tel:+919773682082" className="hover:text-pink-400 transition-colors">+91 9773682082</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <a href="mailto:muasacademy.ranchi@gmail.com" className="text-gray-400 hover:text-pink-400 transition-colors">
                    muasacademy.ranchi@gmail.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-pink-400 flex-shrink-0" />
                  <p className="text-gray-400">
                    Mon - Sat: 10:00 AM - 7:00 PM
                  </p>
                </div>
              </div>
            </div>

            {/* Column 4 - Legal */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-white">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/terms-and-conditions" className="text-gray-400 hover:text-pink-400 transition-colors">Terms & Conditions</Link></li>
                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-pink-400 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/refund-policy" className="text-gray-400 hover:text-pink-400 transition-colors">Refund & Cancellation Policy</Link></li>
              </ul>
              
              {/* Login Button */}
              <div className="pt-4">
                <Button
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white text-sm"
                  onClick={() => setShowLoginModal(true)}
                >
                  Login Portal
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="px-4 pb-8">
          <div className="container mx-auto">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps?q=C-127,+Harmu+Housing+Colony,+Harmu+Ranchi+834002&output=embed"
                width="100%" 
                height="250" 
                style={{ border: 0 }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="MUAS Academy Location"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
              <p className="text-gray-400">
                © {new Date().getFullYear()} MUAS Academy. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-gray-400">
                <Link to="/terms-and-conditions" className="hover:text-pink-400 transition-colors">Terms</Link>
                <span className="hidden md:inline">|</span>
                <Link to="/privacy-policy" className="hover:text-pink-400 transition-colors">Privacy</Link>
                <span className="hidden md:inline">|</span>
                <Link to="/refund-policy" className="hover:text-pink-400 transition-colors">Refund Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
            <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">Login</h2>
            
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              {["student", "teacher", "admin"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setLoginTab(tab)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                    loginTab === tab
                      ? "bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Form */}
            <div className="space-y-4">
              {loginTab === "admin" ? (
                <div>
                  <Label>Username</Label>
                  <Input
                    placeholder="Enter username"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  />
                </div>
              ) : (
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    placeholder="Enter phone number"
                    value={loginForm.phone}
                    onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                  />
                </div>
              )}
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                />
              </div>
              <Button
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
                onClick={handleLogin}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? "Logging in..." : "Login"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
